import { fcIngot as ingot, fcIngotDefs } from '../firecrackers';
import { body, coverBg, edge, fade, mix, n, rng, shade, shadowInk, star, tint, warm, type ArtPal } from './kit';

/**
 * Firecrackers (鞭炮): a 5 × 5 grid of red envelopes (a plain pattern — no 福, no glyphs), a few already open with gold
 * ingots (元宝, the same ingot as the game — art/firecrackers.ts), one ingot popping out of the centre with a burst of sparks.
 * Placeholder art (no text) until the final graphics — see docs/art-prompts.md (no clocks, no green hats,
 * no white/black mourning colours, no glyphs).
 */
export function firecrackersCover(P: ArtPal, p: string): string {
  const cx = 440;
  const cy = 200;
  const top = P.dark ? mix(P.bg, P.red, 0.1) : tint(P.red, 0.9);
  const mid = P.dark ? mix(P.panel, P.orange, 0.08) : tint(P.gold, 0.9);
  const bg = coverBg(p, P, top, mid, P.bg, [cx, cy]);
  const red = P.red;
  const gold = warm(P);
  const pocket = shade(red, P.dark ? 0.55 : 0.4);

  // 5 × 5 envelopes, pitch 56 → grid 276 × 276 around (cx, cy), tilted a little for life
  const pitch = 56;
  const w = 44;
  const h = 50;
  const open = new Set([6, 8, 12, 16, 19]);
  const HERO = 12;
  let opened = '';
  let bodies = '';
  let borders = '';
  let flaps = '';
  let clasps = '';
  let diamonds = '';
  for (let i = 0; i < 25; i++) {
    const x = cx + ((i % 5) - 2) * pitch;
    const y = cy + (Math.floor(i / 5) - 2) * pitch;
    const x0 = x - w / 2;
    const y0 = y - h / 2;
    if (open.has(i)) {
      // opened envelope: the dark pocket, flap folded up, an ingot inside (the centre one pops out — drawn later)
      opened +=
        `<rect x="${x0}" y="${y0}" width="${w}" height="${h}" rx="5" fill="${pocket}" stroke="${edge(red, P)}" stroke-width="1.5"/>` +
        `<path d="M${x0 + 2} ${y0}L${x} ${y0 - 12}L${x0 + w - 2} ${y0}Z" fill="${shade(red, 0.2)}" stroke="${edge(red, P)}" stroke-width="1.5" stroke-linejoin="round"/>` +
        `<ellipse cx="${x}" cy="${y + 4}" rx="20" ry="14" fill="url(#${p}gw)"/>` +
        (i === HERO ? '' : `<g transform="translate(${x} ${y + 6}) scale(.85)">${ingot(P, p)}</g>`);
      continue;
    }
    // closed envelope: red body, gold inset border, flap and a round gold clasp with a diamond (no glyph);
    // drawn in layers with shared attributes to keep the SVG small
    bodies += `<rect x="${x0}" y="${y0}" width="${w}" height="${h}" rx="5"/>`;
    borders += `<rect x="${x0 + 4}" y="${y0 + 4}" width="${w - 8}" height="${h - 8}" rx="3"/>`;
    flaps += `M${x0 + 1} ${y0 + 2}L${x} ${y0 + 19}L${x0 + w - 1} ${y0 + 2}`;
    clasps += `<circle cx="${x}" cy="${y0 + 19}" r="5.5"/>`;
    diamonds += `M${x} ${y0 + 16}l3 3l-3 3l-3 -3Z`;
  }
  const cells =
    `<g fill="url(#${p}ev)" stroke="${edge(red, P)}" stroke-width="1.5">${bodies}</g>` +
    `<g fill="none" stroke="${gold}" stroke-opacity=".55">${borders}</g>` +
    `<path d="${flaps}" fill="none" stroke="${shade(red, 0.3)}" stroke-width="1.5" stroke-linejoin="round"/>` +
    `<g fill="url(#${p}ig)" stroke="${edge(P.gold, P)}">${clasps}</g>` +
    `<path d="${diamonds}" fill="${red}"/>` +
    opened; // opened envelopes on top (their raised flaps overlap the row above)

  // the hero ingot popping out of the centre envelope, with sparks around it
  const hx = cx;
  const hy = cy - 34;
  const r = rng(58);
  let sparks = '';
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2 + (r() - 0.5) * 0.4;
    const d0 = 46 + r() * 8;
    const d1 = d0 + 16 + r() * 16;
    const c = i % 3 === 0 ? P.orange : gold;
    sparks += `<path d="M${n(hx + Math.cos(a) * d0)} ${n(hy + Math.sin(a) * d0 * 0.85)}L${n(hx + Math.cos(a) * d1)} ${n(hy + Math.sin(a) * d1 * 0.85)}" stroke="${c}" stroke-width="${n(2.5 + r() * 1.5)}" stroke-linecap="round"/>`;
  }
  for (let i = 0; i < 9; i++) {
    const a = r() * Math.PI * 2;
    const d = 70 + r() * 70;
    const sx = hx + Math.cos(a) * d * 1.3;
    const sy = hy + Math.sin(a) * d * 0.8;
    sparks += `<polygon points="${star(sx, sy, 4 + r() * 5, 1.6 + r(), 4)}" fill="${i % 2 ? gold : tint(gold, 0.35)}"/>`;
  }
  for (let i = 0; i < 14; i++) {
    const a = r() * Math.PI * 2;
    const d = 60 + r() * 110;
    sparks += `<circle cx="${n(hx + Math.cos(a) * d * 1.3)}" cy="${n(hy + Math.sin(a) * d * 0.75)}" r="${n(1.5 + r() * 2)}" fill="${i % 3 ? gold : P.orange}" fill-opacity="${n(0.55 + r() * 0.4)}"/>`;
  }

  const defs =
    `<defs>${bg.defs}` +
    fade(`${p}sh`, shadowInk(P), P.dark ? 0.5 : 0.16) +
    body(`${p}ev`, red, 0.3, 0.35, 0.6, 1) +
    fcIngotDefs(P, p) +
    fade(`${p}gw`, gold, P.dark ? 0.55 : 0.45) +
    fade(`${p}hg`, P.dark ? P.gold : '#FFFFFF', P.dark ? 0.5 : 0.85) +
    `</defs>`;

  return (
    defs +
    bg.body +
    `<ellipse cx="${cx + 8}" cy="${cy + 164}" rx="160" ry="16" fill="url(#${p}sh)"/>` +
    `<g transform="rotate(-4 ${cx} ${cy})">${cells}</g>` +
    `<ellipse cx="${hx}" cy="${hy}" rx="92" ry="74" fill="url(#${p}hg)"/>` +
    sparks +
    `<g transform="translate(${hx} ${hy}) scale(1.9)">${ingot(P, p)}</g>`
  );
}
